import { css, styled } from 'styled-components/macro'

import { Overlay } from 'styles/misc'
import { mq } from 'styles/mq'

export const Container = styled.div`
  --slide-width: calc(100vw - 2 * var(--container-spacing));

  ${mq.small} {
    --slide-width: calc(
      100vw - 2 * (var(--container-spacing) + var(--grid-size))
    );
  }

  ${mq.medium} {
    --slide-width: calc(
      100vw - 2 *
        (var(--container-spacing) + var(--grid-size) + var(--gap-size))
    );
  }

  ${mq.large} {
    --slide-width: calc(6 * var(--grid-size) + 5 * var(--gap-size));
  }

  ${mq.extraLarge} {
    --slide-width: calc(100vw - 4 * var(--container-spacing));
  }

  ${Overlay}
  position: fixed;
  display: flex;
  flex-direction: column;
  justify-content: center;
  z-index: 1000;

  .swiper {
    width: 100vw;

    ${mq.smallOnly} {
      margin-top: var(--header-height);
      margin-bottom: auto;
      overflow: visible;
    }
  }

  .swiper-slide {
    width: var(--slide-width);
    box-sizing: content-box;
    padding: 0 calc(var(--gap-size) / 4);

    ${mq.medium} {
      padding: 0 calc(var(--gap-size) / 2);
    }
  }
`

export const Backdrop = styled.button`
  all: unset;
  ${Overlay}
  background-color: rgba(13, 20, 21, 0.99);
`

export const Button = styled.div.attrs({ role: 'button', tabIndex: 0 })`
  position: relative;
  cursor: pointer;
  width: 100%;
  max-height: 100%;
  padding: 5vw calc(var(--grid-size) + var(--gap-size));
  font-size: 1.1rem;

  ${mq.medium} {
    overflow-y: auto;
    font-size: 1.4rem;
  }

  ${mq.extraLarge} {
    font-size: 2.1rem;
    padding: var(--grid-size) calc(var(--grid-size) + var(--gap-size));
  }

  ${mq.smallOnly} {
    padding: var(--grid-size) var(--gap-size);
  }
`

export const VideoTileSlide = styled.div<{ $spacer?: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  aspect-ratio: 16 / 9;
  position: relative;
  border: 1px solid rgba(255, 255, 255, 0.6);
  width: 100%;
  flex-shrink: 0;
  background-color: var(--background-color);
  z-index: 0;

  iframe {
    ${Overlay}
    border: none;
  }

  &::before {
    ${Overlay}
    content: "";
    background: linear-gradient(180deg, rgba(13, 20, 21, 0.5) 0%, #0d1415 100%);
  }

  ${props =>
    props.$spacer &&
    css`
      opacity: 0;
    `}

  img {
    ${Overlay}
    opacity: 0.6;
    z-index: -1;
  }
`

export const Tiles = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  gap: var(--gap-size);
`

export const NavButton = styled.button`
  --button-margin: calc((var(--container-spacing) + var(--grid-size)) / 2);

  all: unset;
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  top: 50%;
  left: var(--button-margin);
  width: 1.5rem;
  aspect-ratio: 1 / 2;
  transform: translate(-50%, -50%);
  border-radius: 0.2rem;
  cursor: pointer;
  background-color: var(--text-color);
  z-index: 1200;

  &::before {
    content: '';
    display: block;
    width: 1rem;
    aspect-ratio: 1;
    border-top: 2px solid var(--background-color);
    border-right: 2px solid var(--background-color);
    margin-right: -0.5rem;
    transform: rotateZ(-135deg);
  }

  &[data-right] {
    left: auto;
    right: var(--button-margin);
    transform: translate(50%, -50%) rotateZ(180deg);
  }

  ${mq.smallOnly} {
    top: calc(var(--header-height) + var(--slide-width) * 0.5625 / 2);
  }

  ${mq.extraSmall} {
    display: none;
  }

  ${mq.medium} {
    width: 3.5rem;
    border-radius: 0.5rem;

    &::before {
      width: 1.5rem;
      margin-right: -1rem;
    }
  }

  ${mq.extraLarge} {
    --button-margin: calc((var(--gap-size) / 2) + var(--grid-size));

    width: 5rem;
    border-radius: 1rem;

    &::before {
      width: 2rem;
    }
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.3;
  }
`

const transition = css`
  transition: 0.5s all;
  transform: translateY(2rem);
  opacity: 0;
  margin-left: 2px;

  .swiper-slide-active & {
    opacity: 1;
    transform: none;

    &:first-child {
      transition-delay: 0.3s;
    }
    &:nth-child(2) {
      transition-delay: 0.4s;
    }
    &:nth-child(3) {
      transition-delay: 0.5s;
    }
  }
`

export const PlayerTitle = styled.h2`
  ${transition}
  position: relative;
  width: 100%;
  text-transform: uppercase;
  line-height: 1.1;
  font-size: 2em;
  margin: 0;

  ${mq.smallOnly} {
    font-size: 1.3em;
  }
`

export const PlayIcon = styled.span`
  ${transition}
  content: '';
  display: block;
  margin-bottom: 2em;
  border-width: 1.2em 2em;
  border-color: transparent;
  border-style: solid;
  border-left-color: white;
`

export const PlayerDescription = styled.p`
  ${transition}
  position: relative;
  width: 100%;
  margin: 1rem 0 0;
  font-size: 1.6rem;
  line-height: 1.4;

  ${mq.smallOnly} {
    position: absolute;
    top: 100%;
    left: 0;
    width: 100%;
    margin: 0;
    max-height: calc(
      100vh - var(--header-height) - (var(--slide-width) * 0.5625)
    );
    max-height: calc(
      100dvh - var(--header-height) - (var(--slide-width) * 0.5625)
    );
    overflow: auto;
    padding: var(--grid-size) 7%;
    font-size: 1.2em;

    &::before,
    &::after {
      content: '';
      display: block;
      height: 1em;
    }
  }
`
